import { reactive } from 'vue';
import { injectable } from 'inversify';
import { FilterKey, FilterOptionInformation, IFilterPayload, IFilterProvider } from '@studyportals/search-filters';
import { Product } from '@studyportals/datalake-event-tracker';
import { FilterOptions } from '../options/FilterOptions';
import { FilterSelection } from '../selection/FilterSelection';
import { EventAggregationService } from '@/platform/EventAggregationService';
import { SearchPageClearFiltersEvent } from '@studyportals/search-page-interface';

@injectable()
export class FilterProvider implements IFilterProvider {
	private readonly filterOptions: FilterOptions;
	private readonly filterSelection: FilterSelection;
	private readonly isCollapsedByKey: { [key: string]: boolean | undefined } = reactive({});

	public readonly trackedProduct = Product.ORGANISATION_SEARCH_PAGE;
	public readonly displayFacets = false;

	private readonly eventAggregationService: EventAggregationService;

	public constructor(
		filterOptions: FilterOptions, 
		filterSelection: FilterSelection, 
		eventAggregationService: EventAggregationService
	) {
		this.filterOptions = filterOptions;
		this.filterSelection = filterSelection;
		this.eventAggregationService = eventAggregationService;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public getFacet(_key: FilterKey, _optionValue: string): number | null {
		return null;
	}

	public toggleFilterExpandability(key: FilterKey): void {
		this.isCollapsedByKey[key] = !this.isCollapsedByKey[key];
	}

	public isFilterCollapsed(key: FilterKey): boolean {
		return this.isCollapsedByKey[key] ?? false;
	}

	public isFilterRequired(key: FilterKey): boolean {
		return this.filterOptions.isRequiredFor(key);
	}

	public getFilterOptionInformation(key: FilterKey): readonly FilterOptionInformation[] {
		return this.filterOptions.getFor(key);
	}

	public getFilterOptionInformationById(key: FilterKey, id: string): FilterOptionInformation | null {
		return this.filterOptions.getForByValue(key, id);
	}

	public getFilterKeySelection(): readonly FilterKey[] {
		return this.filterSelection.getKeys();
	}

	public getFilterSelection(key: FilterKey): readonly string[] {
		return this.filterSelection.getFor(key);
	}

	public async processFilterSelection(payload: IFilterPayload): Promise<void> {
		this.filterSelection.processSelection([payload]);
		return Promise.resolve();
	}

	public async processFiltersSelection(payload: IFilterPayload[]): Promise<void> {
		this.filterSelection.processSelection(payload);
		return Promise.resolve();
	}

	public async clearFilter(key: FilterKey): Promise<void> {
		this.filterSelection.clearFor([key]);

		this.eventAggregationService.publishTo(SearchPageClearFiltersEvent.EventType, new SearchPageClearFiltersEvent([key]));
		
		return Promise.resolve();
	}

	public async clearFilters(keys: FilterKey[]): Promise<void> {
		this.filterSelection.clearFor(keys);

		this.eventAggregationService.publishTo(SearchPageClearFiltersEvent.EventType, new SearchPageClearFiltersEvent(keys));
		
		return Promise.resolve();
	}

	public async clearSelection(payload: IFilterPayload[]): Promise<void> {
		this.filterSelection.clearSelection(payload);

		this.eventAggregationService.publishTo(SearchPageClearFiltersEvent.EventType, new SearchPageClearFiltersEvent((payload.map((_) => _.key))));
		
		return Promise.resolve();
	}
}
